import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';
import { 
  Dumbbell, 
  Utensils, 
  Target, 
  MessageCircle, 
  Users, 
  BarChart3, 
  Zap,
  Trophy,
  ChevronRight,
  Check,
  Flame,
  TrendingDown,
  Star,
  Award
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dfed4268-137d-4b89-bcab-122f53e08876/artifacts/k5gcf49q_BBA%20LOGO.webp";
const HERO_IMAGE = "https://images.unsplash.com/photo-1700784795176-7ff886439d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxpbnRlbnNlJTIwZ3ltJTIwd29ya291dCUyMGF0aGxldGUlMjBkYXJrJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwMzk5NTY2fDA&ixlib=rb-4.1.0&q=85";
const NUTRITION_IMAGE = "https://images.unsplash.com/photo-1636044992119-be0892f9de94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwbWVhbCUyMHByZXAlMjBmaXRuZXNzJTIwbnV0cml0aW9uJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcwMzk5NTcwfDA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMAGE = "https://images.pexels.com/photos/6339323/pexels-photo-6339323.jpeg";

// Custom Logo Component with clean styling
const Logo = ({ className = "h-12 w-auto" }) => (
  <div className={`relative ${className}`} style={{ aspectRatio: '1/1' }}>
    <img 
      src={LOGO_URL} 
      alt="Bodies By Akeem" 
      className="h-full w-auto rounded-full"
      style={{ 
        background: 'transparent',
        filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))'
      }}
    />
  </div>
);

const features = [
  { icon: Dumbbell, title: 'AI WORKOUT BUILDER', desc: 'Generate personalized programs in seconds' },
  { icon: Utensils, title: 'NUTRITION PLANS', desc: 'Custom meal plans for your goals' },
  { icon: Target, title: 'HABIT TRACKING', desc: 'Build consistency with streaks' },
  { icon: MessageCircle, title: '1-1 MESSAGING', desc: 'Direct access to your trainer' },
  { icon: Users, title: 'COMMUNITY', desc: 'Connect with like-minded athletes' },
  { icon: BarChart3, title: 'ANALYTICS', desc: 'Track every metric that matters' },
];

const pricingPlans = [
  {
    name: 'FOUNDATION',
    price: '$19.99',
    period: '/month',
    features: ['Workout Library', 'Basic Tracking', 'Community Access', 'Mobile App'],
    highlighted: false
  },
  {
    name: 'PERFORMANCE',
    price: '$39.99',
    period: '/month',
    features: ['All Foundation Features', 'AI Workout Builder', 'AI Meal Plans', '1-1 Messaging', 'Priority Support'],
    highlighted: true
  },
  {
    name: 'BLACK CARD',
    price: '$59.99',
    period: '/month',
    features: ['All Performance Features', 'Live Coaching', 'Custom Programs', 'HSA/FSA Eligible', 'Unlimited Everything'],
    highlighted: false
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topTransformations, setTopTransformations] = useState([]);
  const [transformationOfMonth, setTransformationOfMonth] = useState(null);

  useEffect(() => {
    fetchTransformations();
  }, []);

  const fetchTransformations = async () => {
    try {
      const [topRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/transformations/top`),
        axios.get(`${API}/transformations/monthly`)
      ]);
      setTopTransformations(topRes.data.slice(0, 3));
      setTransformationOfMonth(monthlyRes.data);
    } catch (error) {
      console.error('Error fetching transformations:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Noise Overlay */}
      <div className="noise-overlay"></div>
      
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <Logo className="h-12" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-300 hover:text-white transition-colors text-sm uppercase tracking-wider">Features</a>
              <a href="#transformations" className="text-gray-300 hover:text-white transition-colors text-sm uppercase tracking-wider">Results</a>
              <a href="#pricing" className="text-gray-300 hover:text-white transition-colors text-sm uppercase tracking-wider">Pricing</a>
              <a href="#about" className="text-gray-300 hover:text-white transition-colors text-sm uppercase tracking-wider">About</a>
            </nav>
            
            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  data-testid="dashboard-btn"
                  onClick={() => navigate('/dashboard')}
                  className="bg-primary hover:bg-red-700 text-white uppercase font-semibold tracking-wide"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    data-testid="login-btn"
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    className="text-white hover:bg-white/10"
                  >
                    Login
                  </Button>
                  <Button 
                    data-testid="get-started-btn"
                    onClick={() => navigate('/register')}
                    className="bg-primary hover:bg-red-700 text-white uppercase font-semibold tracking-wide"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Athlete" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <Zap className="w-5 h-5 text-primary" />
              <span className="text-primary uppercase tracking-widest text-sm font-semibold">Transform Your Body</span>
            </div>
            
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white uppercase leading-none mb-6">
              BUILD YOUR <span className="text-primary">ULTIMATE</span> PHYSIQUE
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 max-w-xl">
              AI-powered workout programming, personalized nutrition plans, and direct access to elite coaching. Everything you need to achieve your fitness goals.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                data-testid="hero-get-started-btn"
                onClick={() => navigate('/register')}
                className="bg-primary hover:bg-red-700 text-white text-lg px-8 py-6 uppercase font-bold tracking-wide"
              >
                Start Your Journey <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                data-testid="hero-learn-more-btn"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 uppercase tracking-wide"
              >
                Learn More
              </Button>
            </div>
            
            <div className="flex items-center gap-8 mt-12">
              <div>
                <div className="font-mono text-3xl font-bold text-white">1000+</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">Active Members</div>
              </div>
              <div className="w-px h-12 bg-gray-700"></div>
              <div>
                <div className="font-mono text-3xl font-bold text-white">50K+</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">Workouts Completed</div>
              </div>
              <div className="w-px h-12 bg-gray-700"></div>
              <div>
                <div className="font-mono text-3xl font-bold text-white">4.9</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">Star Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl text-white uppercase mb-4">
              EVERYTHING YOU NEED TO <span className="text-primary">SUCCEED</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              A complete fitness ecosystem designed to help you reach your goals faster than ever before.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="card-tactical group cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transformations Section */}
      <section id="transformations" className="py-24 bg-gradient-to-b from-transparent to-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Award className="w-6 h-6 text-primary" />
              <span className="text-primary uppercase tracking-widest text-sm font-semibold">Real Results</span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl text-white uppercase mb-4">
              CLIENT <span className="text-primary">TRANSFORMATIONS</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              See the incredible results our members have achieved. Your transformation could be next.
            </p>
          </div>

          {/* Transformation of the Month */}
          {transformationOfMonth && (
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                <span className="text-yellow-500 uppercase tracking-wider text-sm font-semibold">
                  Transformation of the Month
                </span>
              </div>
              
              <div className="card-tactical border-yellow-500/30 bg-gradient-to-br from-yellow-500/5 to-transparent p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  {/* Before/After Images */}
                  <div className="grid grid-cols-2 gap-4">
                    {transformationOfMonth.before_photo ? (
                      <div className="relative">
                        <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white z-10">
                          BEFORE
                        </div>
                        <img 
                          src={transformationOfMonth.before_photo} 
                          alt="Before" 
                          className="w-full aspect-[3/4] object-cover rounded-sm border border-border"
                        />
                        <div className="text-center mt-2 text-gray-400 text-sm">
                          {transformationOfMonth.before_weight}kg
                        </div>
                      </div>
                    ) : (
                      <div className="w-full aspect-[3/4] bg-secondary/50 rounded-sm border border-border flex items-center justify-center">
                        <span className="text-gray-500">Before</span>
                      </div>
                    )}
                    
                    {transformationOfMonth.after_photo ? (
                      <div className="relative">
                        <div className="absolute top-2 left-2 bg-primary px-2 py-1 rounded text-xs text-white z-10">
                          AFTER
                        </div>
                        <img 
                          src={transformationOfMonth.after_photo} 
                          alt="After" 
                          className="w-full aspect-[3/4] object-cover rounded-sm border border-primary"
                        />
                        <div className="text-center mt-2 text-white text-sm font-semibold">
                          {transformationOfMonth.after_weight}kg
                        </div>
                      </div>
                    ) : (
                      <div className="w-full aspect-[3/4] bg-secondary/50 rounded-sm border border-primary flex items-center justify-center">
                        <span className="text-gray-500">After</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Stats & Testimonial */}
                  <div>
                    <h3 className="font-heading text-3xl text-white uppercase mb-2">
                      {transformationOfMonth.title || `${transformationOfMonth.user?.first_name}'s Journey`}
                    </h3>
                    
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-4 bg-secondary/30 rounded-sm">
                        <TrendingDown className="w-5 h-5 text-green-500 mx-auto mb-1" />
                        <div className="font-mono text-2xl text-white font-bold">
                          {transformationOfMonth.weight_lost?.toFixed(1) || 0}kg
                        </div>
                        <div className="text-xs text-gray-400">Lost</div>
                      </div>
                      <div className="text-center p-4 bg-secondary/30 rounded-sm">
                        <Target className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                        <div className="font-mono text-2xl text-white font-bold">
                          {transformationOfMonth.body_fat_lost?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-gray-400">Body Fat</div>
                      </div>
                      <div className="text-center p-4 bg-secondary/30 rounded-sm">
                        <Flame className="w-5 h-5 text-primary mx-auto mb-1" />
                        <div className="font-mono text-2xl text-white font-bold">
                          {transformationOfMonth.user?.current_streak || 0}
                        </div>
                        <div className="text-xs text-gray-400">Day Streak</div>
                      </div>
                    </div>
                    
                    {transformationOfMonth.testimonial && (
                      <blockquote className="border-l-2 border-primary pl-4 text-gray-300 italic mb-6">
                        "{transformationOfMonth.testimonial}"
                      </blockquote>
                    )}
                    
                    <div className="flex items-center gap-2 text-gray-400">
                      <span className="capitalize">{transformationOfMonth.fitness_goal?.replace('_', ' ')}</span>
                      <span>•</span>
                      <span>{transformationOfMonth.user?.first_name} {transformationOfMonth.user?.last_initial}.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Top Transformations Grid */}
          {topTransformations.length > 0 && (
            <div>
              <h3 className="font-heading text-xl text-white uppercase mb-6">More Success Stories</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {topTransformations.map((transformation, index) => (
                  <div key={index} className="card-tactical group">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                        <span className="font-heading text-lg text-primary">
                          {transformation.first_name?.[0]}{transformation.last_initial}
                        </span>
                      </div>
                      <div>
                        <div className="text-white font-medium">
                          {transformation.first_name} {transformation.last_initial}.
                        </div>
                        <div className="text-xs text-gray-400 capitalize">
                          {transformation.fitness_goal?.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="text-center p-3 bg-secondary/30 rounded-sm">
                        <div className="font-mono text-xl text-green-500 font-bold flex items-center justify-center gap-1">
                          <TrendingDown className="w-4 h-4" />
                          {transformation.weight_lost?.toFixed(1)}kg
                        </div>
                        <div className="text-xs text-gray-400">Lost</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/30 rounded-sm">
                        <div className="font-mono text-xl text-primary font-bold flex items-center justify-center gap-1">
                          <Flame className="w-4 h-4" />
                          {transformation.current_streak}
                        </div>
                        <div className="text-xs text-gray-400">Streak</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1 text-yellow-500">
                        <Trophy className="w-4 h-4" />
                        {transformation.badges_count} badges
                      </div>
                      <div className="text-gray-400">
                        {transformation.total_points?.toLocaleString()} pts
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No transformations yet - show placeholder */}
          {topTransformations.length === 0 && !transformationOfMonth && (
            <div className="text-center py-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="card-tactical">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="text-white font-medium">Your Story Here</div>
                        <div className="text-xs text-gray-400">Be the next success</div>
                      </div>
                    </div>
                    <div className="text-center py-8 text-gray-500">
                      Start your transformation today
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center mt-12">
            <Button 
              data-testid="start-transformation-btn"
              onClick={() => navigate('/register')}
              className="bg-primary hover:bg-red-700 text-white uppercase font-semibold px-8"
            >
              Start Your Transformation
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* App Preview Section */}
      <section className="py-24 bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl sm:text-5xl text-white uppercase mb-6">
                TRACK YOUR <span className="text-primary">PROGRESS</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Connect your favorite fitness devices and apps. Sync with Apple Health, Google Fit, Garmin, Fitbit, and more for comprehensive data tracking.
              </p>
              
              <div className="space-y-4">
                {['Apple Health', 'Google Fit', 'Garmin', 'Fitbit', 'Strava'].map((app, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-secondary/50 rounded-sm">
                    <div className="w-8 h-8 bg-primary/20 rounded-sm flex items-center justify-center">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-white font-medium">{app}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={NUTRITION_IMAGE} 
                alt="Nutrition tracking" 
                className="rounded-md border border-border w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-md p-4">
                <div className="flex items-center gap-3">
                  <Trophy className="w-8 h-8 text-primary" />
                  <div>
                    <div className="text-white font-bold">7 Day Streak!</div>
                    <div className="text-sm text-gray-400">Keep it going!</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl text-white uppercase mb-4">
              CHOOSE YOUR <span className="text-primary">PLAN</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Flexible pricing to match your fitness journey. All plans include a 7-day free trial.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index}
                className={`card-tactical relative ${plan.highlighted ? 'border-primary' : ''}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-white text-xs uppercase font-bold px-3 py-1 rounded-sm">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <h3 className="font-heading text-2xl text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-mono text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-gray-400">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Button 
                  data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  onClick={() => navigate('/register')}
                  className={`w-full uppercase font-semibold ${
                    plan.highlighted 
                      ? 'bg-primary hover:bg-red-700 text-white' 
                      : 'bg-secondary hover:bg-secondary/80 text-white'
                  }`}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="about" className="py-24 bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={COMMUNITY_IMAGE} 
                alt="Community" 
                className="rounded-md border border-border w-full"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="font-heading text-4xl sm:text-5xl text-white uppercase mb-6">
                JOIN THE <span className="text-primary">COMMUNITY</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Connect with thousands of athletes, share your progress, get motivated by others, and be part of something bigger than yourself.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-secondary/50 rounded-sm p-4">
                  <div className="font-mono text-2xl font-bold text-white">24/7</div>
                  <div className="text-sm text-gray-400">Support Available</div>
                </div>
                <div className="bg-secondary/50 rounded-sm p-4">
                  <div className="font-mono text-2xl font-bold text-white">100+</div>
                  <div className="text-sm text-gray-400">Weekly Challenges</div>
                </div>
              </div>
              
              <Button 
                data-testid="join-community-btn"
                onClick={() => navigate('/register')}
                className="bg-primary hover:bg-red-700 text-white uppercase font-semibold"
              >
                Join Now <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl text-white uppercase mb-6">
            READY TO <span className="text-primary">TRANSFORM</span>?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Start your 7-day free trial today. No credit card required.
          </p>
          <Button 
            data-testid="cta-start-btn"
            onClick={() => navigate('/register')}
            size="lg"
            className="bg-primary hover:bg-red-700 text-white text-lg px-10 py-6 uppercase font-bold tracking-wide"
          >
            Start Free Trial <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Logo className="h-10" />
            </div>
            
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
            
            <div className="text-sm text-gray-500">
              © 2024 Bodies By Akeem. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
